import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Send, Clock, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { parseVideoUrl } from "@/lib/video";
import { adminBtnPrimary, adminBtnGhost, adminField, adminLabel, StatusBadge } from "@/components/admin/ui";

function toETInput(iso) {
  if (!iso) return "";
  try {
    const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false })
      .formatToParts(new Date(iso)).reduce((a, x) => ((a[x.type] = x.value), a), {});
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
  } catch { return ""; }
}

const EMPTY = {
  title: "", url: "", topic: "", tags: [], duration: "", description: "", summary: "",
  takeaways: [], transcript: "", chapters: [], related_article_ids: [], status: "draft",
  publish_at: "", featured: false, thumbnail: "", seo_title: "", seo_description: "", slug: "",
};

export default function VideoEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [articles, setArticles] = useState([]);
  const [loaded, setLoaded] = useState(isNew);
  const [savedId, setSavedId] = useState(isNew ? null : id);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/articles").then((r) => setArticles(r.data.filter((a) => a.status === "published"))).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/videos/${id}`).then((r) => {
      const v = r.data;
      const url = v.provider === "youtube" ? `https://www.youtube.com/watch?v=${v.video_id}` : `https://vimeo.com/${v.video_id}`;
      setForm({
        title: v.title || "", url, topic: v.topic || "", tags: v.tags || [], duration: v.duration || "",
        description: v.description || "", summary: v.summary || "", takeaways: v.takeaways || [],
        transcript: v.transcript || "", chapters: v.chapters || [], related_article_ids: v.related_article_ids || [],
        status: v.status || "draft", publish_at: toETInput(v.publish_at), featured: !!v.featured,
        thumbnail: v.thumbnail || "", seo_title: v.seo_title || "", seo_description: v.seo_description || "", slug: v.slug || "",
      });
      setLoaded(true);
    }).catch(() => { toast.error("Could not load video"); navigate("/admin/videos"); });
  }, [id, isNew, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const preview = parseVideoUrl(form.url);

  const persist = async (overrides = {}) => {
    const payload = { ...form, ...overrides };
    if (!payload.title.trim()) { toast.error("Add a title first."); return null; }
    if (!parseVideoUrl(payload.url)) { toast.error("Enter a valid YouTube or Vimeo link."); return null; }
    setSaving(true);
    try {
      let res;
      if (savedId) res = await api.put(`/admin/videos/${savedId}`, payload);
      else res = await api.post("/admin/videos", payload);
      setSavedId(res.data.id);
      set("status", res.data.status);
      set("slug", res.data.slug);
      if (isNew && res.data.id) window.history.replaceState(null, "", `/admin/videos/${res.data.id}`);
      toast.success("Saved");
      return res.data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      return null;
    } finally { setSaving(false); }
  };

  const doPublish = () => persist({ status: "published", publish_at: "" });
  const doSchedule = () => { if (!form.publish_at) { toast.error("Choose a publish time (ET)."); return; } persist({ status: "scheduled" }); };

  const setChapter = (i, k, v) => set("chapters", form.chapters.map((c, idx) => idx === i ? { ...c, [k]: v } : c));

  if (!loaded) return <p className="py-20 text-center font-sans text-steel-400">Loading…</p>;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <button onClick={() => navigate("/admin/videos")} className="flex items-center gap-2 font-sans text-sm text-steel-600 hover:text-gold-600"><ArrowLeft className="h-4 w-4" /> All videos</button>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={form.status} />
          <button onClick={() => persist()} disabled={saving} className={adminBtnGhost} data-testid="video-save-button"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</button>
          <button onClick={doPublish} disabled={saving} className={adminBtnPrimary} data-testid="video-publish-button"><Send className="h-4 w-4" /> Publish</button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <div>
            <label className={adminLabel}>Title</label>
            <input value={form.title} onChange={(e) => set("title", e.target.value)} className={adminField} data-testid="video-title" />
          </div>
          <div>
            <label className={adminLabel}>YouTube or Vimeo link</label>
            <input value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://www.youtube.com/watch?v=…" className={adminField} data-testid="video-url" />
            {form.url && (preview ? (
              <div className="mt-3 aspect-video max-w-md overflow-hidden bg-ink">
                <img src={preview.provider === "youtube" ? `https://img.youtube.com/vi/${preview.id}/hqdefault.jpg` : ""} alt="" className="h-full w-full object-cover" />
              </div>
            ) : <p className="mt-1 font-sans text-xs text-destructive">Only YouTube and Vimeo links are accepted.</p>)}
          </div>
          <div>
            <label className={adminLabel}>Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} className={adminField} data-testid="video-description" />
          </div>
          <div>
            <label className={adminLabel}>Summary</label>
            <textarea rows={3} value={form.summary} onChange={(e) => set("summary", e.target.value)} className={adminField} data-testid="video-summary" />
          </div>
          <div>
            <label className={adminLabel}>Key takeaways <span className="text-steel-400">(one per line)</span></label>
            <textarea rows={4} value={form.takeaways.join("\n")} onChange={(e) => set("takeaways", e.target.value.split("\n").map((t) => t.trim()).filter(Boolean))} className={adminField} data-testid="video-takeaways" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className={adminLabel}>Chapters</label>
              <button onClick={() => set("chapters", [...form.chapters, { time: "", title: "" }])} className="flex items-center gap-1 font-sans text-xs text-gold-600"><Plus className="h-3 w-3" /> Add chapter</button>
            </div>
            {form.chapters.map((c, i) => (
              <div key={i} className="mt-2 flex gap-2">
                <input value={c.time} onChange={(e) => setChapter(i, "time", e.target.value)} placeholder="00:00" className={`${adminField} w-24`} />
                <input value={c.title} onChange={(e) => setChapter(i, "title", e.target.value)} placeholder="Chapter title" className={adminField} />
                <button onClick={() => set("chapters", form.chapters.filter((_, idx) => idx !== i))} className="text-steel-400 hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div>
            <label className={adminLabel}>Transcript</label>
            <textarea rows={6} value={form.transcript} onChange={(e) => set("transcript", e.target.value)} className={adminField} data-testid="video-transcript" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="border border-ink/10 bg-cream-100 p-5 space-y-3">
            <label className={adminLabel}>Publishing</label>
            <div className="flex gap-2">
              {["draft", "published", "scheduled"].map((st) => (
                <button key={st} onClick={() => set("status", st)} className={`flex-1 rounded-sm border px-2 py-1.5 font-sans text-xs capitalize ${form.status === st ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink/70"}`} data-testid={`video-status-${st}`}>{st}</button>
              ))}
            </div>
            {form.status === "scheduled" && (
              <div>
                <label className="font-sans text-xs text-steel-500">Publish at (Eastern Time)</label>
                <input type="datetime-local" value={form.publish_at} onChange={(e) => set("publish_at", e.target.value)} className={adminField} data-testid="video-publish-at" />
                <button onClick={doSchedule} className={`mt-2 w-full ${adminBtnGhost}`}><Clock className="h-4 w-4" /> Schedule</button>
              </div>
            )}
            <label className="flex items-center gap-2 font-sans text-sm text-ink"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="video-featured" /> Feature this video</label>
          </div>

          <div className="border border-ink/10 bg-cream-100 p-5 space-y-4">
            <div>
              <label className={adminLabel}>Topic</label>
              <input value={form.topic} onChange={(e) => set("topic", e.target.value)} placeholder="e.g. Strategic Planning" className={adminField} data-testid="video-topic" />
            </div>
            <div>
              <label className={adminLabel}>Duration</label>
              <input value={form.duration} onChange={(e) => set("duration", e.target.value)} placeholder="e.g. 8:24" className={adminField} data-testid="video-duration" />
            </div>
            <div>
              <label className={adminLabel}>Tags <span className="text-steel-400">(comma separated)</span></label>
              <input value={form.tags.join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className={adminField} />
            </div>
          </div>

          {articles.length > 0 && (
            <div className="border border-ink/10 bg-cream-100 p-5">
              <label className={adminLabel}>Supporting articles</label>
              <div className="mt-3 max-h-48 space-y-2 overflow-y-auto">
                {articles.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 font-sans text-sm text-ink">
                    <input type="checkbox" checked={form.related_article_ids.includes(a.id)} onChange={(e) => set("related_article_ids", e.target.checked ? [...form.related_article_ids, a.id] : form.related_article_ids.filter((x) => x !== a.id))} />
                    <span className="line-clamp-1">{a.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="border border-ink/10 bg-cream-100 p-5 space-y-4">
            <label className={adminLabel}>URL &amp; SEO</label>
            <div>
              <label className="font-sans text-xs text-steel-500">Slug</label>
              <input value={form.slug} onChange={(e) => set("slug", e.target.value)} className={adminField} data-testid="video-slug" />
            </div>
            <div>
              <label className="font-sans text-xs text-steel-500">SEO title</label>
              <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={adminField} />
            </div>
            <div>
              <label className="font-sans text-xs text-steel-500">SEO description</label>
              <textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={adminField} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
